import { any, reduce, reverse } from 'ramda';
import React, { FunctionComponent } from 'react';
import { Form } from 'react-bootstrap';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { ReferenceType } from '../../common/enums/ReferenceType';
import { getCountersSum, getStep3Text, refCounterTypes, refTypes } from '../../common/helpers/refCountersHelper';
import { IBindingAction, IBindingCallback1 } from '../../common/models/callback';
import { IRefReqCounters } from '../../common/models/referenceRequest/IRefReqCounters';
import Counter from '../Counter';
import CounterItem from '../CounterItem';
import Icon from '../Icon';
import RegularButton from '../RegularButton';
import styles from './styles.module.scss';

interface IProps {
  isAdvanced: boolean;
  refCounters: IRefReqCounters;
  intl: IntlShape;
  disabledTypes?: ReferenceType[];
  isAddReferencesEnabled?: boolean;
  withBackground?: boolean;
  changeCounters: IBindingCallback1<IRefReqCounters>;
  enableAddReferences?: IBindingAction;
  disableCounter?: IBindingCallback1<ReferenceType>;
  toggleAdvancedReq: IBindingCallback1<boolean>;
}

const RefReqCounters: FunctionComponent<IProps> = ({
  intl,
  isAdvanced,
  refCounters,
  disabledTypes = [],
  isAddReferencesEnabled = false,
  withBackground = false,
  disableCounter = () => null,
  changeCounters,
  toggleAdvancedReq,
  enableAddReferences
}) => {
  const getCountersText = () => {
    const text = getStep3Text(refCounters, intl);
    return text
      ? (
        <>
          ,
          &nbsp;
          <span className="text-lowercase">
            <FormattedMessage id="candidateForm.overview.provided" defaultMessage="including at least" />
            &nbsp;
            <strong>{text}</strong>
          </span>
        </>
      )
      : null;
  };

  const changeTotalCounter = (value: number) => {
    const countersSum = getCountersSum(refCounters);
    let updatedRefCounters = refCounters;
    let diff = countersSum - value;

    if (diff > 0) {
      updatedRefCounters = reduce<keyof IRefReqCounters, IRefReqCounters>((acc, key) => {
        const currentValue = refCounters[key];
        if (!diff || !currentValue) return { ...acc, [key]: currentValue };
        diff -= 1;
        return { ...acc, [key]: currentValue - 1 };
      }, {} as IRefReqCounters, reverse(refTypes));
    }

    changeCounters({ ...updatedRefCounters, totalCount: value });
  };

  const onChangeCounter = (value: number, type: keyof IRefReqCounters) => {
    const updatedRefCounters = { ...refCounters, [type]: value };
    const countersSum = getCountersSum(updatedRefCounters);

    changeCounters({
      ...updatedRefCounters,
      totalCount: (countersSum > updatedRefCounters.totalCount
        ? updatedRefCounters.totalCount + 1
        : updatedRefCounters.totalCount)
    });
  };

  const onDisableCounter = (type: keyof IRefReqCounters) => disableCounter(refCounterTypes[type]);

  return (
    <div className={styles.step3Container}>
      <header className={styles.step3Header}>
        <div>
          <div className={styles.step3HeaderText}>
            <FormattedMessage
              id="step3.header"
              defaultMessage="How many references (minimum) should the candidate provide?"
            />
          </div>
          <div className="d-flex align-items-center">
            <Counter
              disableMinus={refCounters.totalCount === 1}
              value={refCounters.totalCount}
              onChange={changeTotalCounter}
            />
            <div className={styles.totalCounterText}>
              <strong>
                <FormattedMessage id="card.references.title" defaultMessage="References" />
              </strong>
              {isAdvanced && getCountersText()}
            </div>
          </div>
        </div>
        {isAddReferencesEnabled && enableAddReferences && (
          <div className="d-flex flex-column align-items-end">
            <div className="d-flex align-items-center mb-4">
              <Icon className="mr-2" icon="info" />
              <span className={`t-14 ${styles.hint}`}>
                <FormattedMessage
                  id="step3.addReferencesTip"
                  defaultMessage="Already have references’ contact info?"
                />
              </span>
            </div>
            <RegularButton
              textIntlId="step3.addReferences"
              defaultMessage="Enter references myself"
              icon={<Icon icon="plus" />}
              variant="tertiary"
              onClick={enableAddReferences}
            />
          </div>
        )}
      </header>
      <main className={`${styles.step3Body} ${withBackground ? styles.step3BodyBackground : ''}`}>
        <div className={`${styles.advancedToggler} d-flex align-items-center ${isAdvanced ? 'mb-4' : 'mb-3'}`}>
          <Form.Check
            className="switch-input"
            type="switch"
            id="advanced"
            checked={isAdvanced}
            label=""
            onChange={() => toggleAdvancedReq(!isAdvanced)}
          />
          <div className="ml-3">
            <FormattedMessage id="step3.advanced" defaultMessage="Require specific reference types" />
          </div>
        </div>
        {isAdvanced ? (
          <div>
            {refTypes.map(type => (
              <CounterItem
                key={type}
                value={refCounters[type]}
                prefix={type}
                onChange={val => onChangeCounter(val, type)}
                onDisable={onDisableCounter}
                disabled={any(refType => refType === refCounterTypes[type], disabledTypes)}
              />
            ))}
          </div>
        ) : (
          <span className={`t-14 ${styles.hint}`}>
            <FormattedMessage
              id="step3.hint"
              defaultMessage="This will instruct candidates to provide a minimum number of Managers, Peers, etc."
            />
          </span>
        )}
      </main>
    </div>
  );
};

export default injectIntl(RefReqCounters);
